import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faClipboardCheck,
    faPhone,
    faPersonWalking
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../../components/ConfirmationModal';

ChartJS.register(
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    Title, Tooltip, Legend, ArcElement, Filler
);


const SurveyStats = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canClickBatch = ['administrateur_general', 'administrateur_general_superieur', 'chef_terrain_superieur'].includes(user?.role);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [terminateModalOpen, setTerminateModalOpen] = useState(false);
    const [togglingTerminate, setTogglingTerminate] = useState(false);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/cati/surveys/${id}/stats`);
                if (res.data.success) {
                    setData(res.data.data);
                } else {
                    setError(res.data.message || 'Erreur');
                }
            } catch (err) {
                setError(err.response?.status === 404 ? 'Enquête introuvable' : 'Erreur lors du chargement');
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-500">Chargement des statistiques...</p>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600 mb-4">{error || 'Données indisponibles'}</p>
                    <button onClick={() => navigate(-1)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retour</button>
                </div>
            </div>
        );
    }

    const { survey, completion, control } = data;

    // "Terminer" override: hide this survey from all progress dashboards (used when a control was left
    // active). Only CTS / CES / AG / AGS may toggle it. Reversible here via "Réactiver".
    const canTerminateProgress = ['chef_terrain_superieur', 'charge_etude_superieur', 'administrateur_general', 'administrateur_general_superieur'].includes(user?.role);
    const isProgressTerminated = !!survey.progress_terminated_at;
    const applyTerminateToggle = async (terminate) => {
        try {
            setTogglingTerminate(true);
            await api.post(`/cati/surveys/${id}/${terminate ? 'terminate' : 'reactivate'}-progress`);
            setData(prev => prev ? { ...prev, survey: { ...prev.survey, progress_terminated_at: terminate ? new Date().toISOString() : null } } : prev);
            setTerminateModalOpen(false);
        } catch (err) {
            console.error('Error toggling survey progress termination:', err);
        } finally {
            setTogglingTerminate(false);
        }
    };
    const isF2f = survey.poste_qcode === 'ref';
    const completionPct = completion.target ? Math.min(100, Math.round((completion.total / completion.target) * 100)) : null;

    // --- Chart configs ---

    // Daily completions (Line)
    const dailyLineData = {
        labels: completion.daily.map(d => {
            const dt = new Date(d.date);
            return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
        }),
        datasets: [
            {
                label: 'Complétions',
                data: completion.daily.map(d => d.completes),
                borderColor: isF2f ? 'rgb(59, 130, 246)' : 'rgb(220, 38, 38)',
                backgroundColor: isF2f ? 'rgba(59, 130, 246, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                fill: true,
                tension: 0.3,
                pointRadius: completion.daily.length > 60 ? 0 : 3,
            },
            ...(completion.target ? [{
                label: 'Moy. cible/jour',
                data: completion.daily.map(() => {
                    const totalDays = completion.daily.length || 1;
                    return Math.round(completion.target / totalDays);
                }),
                borderColor: 'rgba(156, 163, 175, 0.6)',
                borderDash: [6, 4],
                pointRadius: 0,
                fill: false,
            }] : []),
        ],
    };

    const dailyLineOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } },
        scales: {
            y: { beginAtZero: true, ticks: { font: { size: 10 } } },
            x: { ticks: { font: { size: 9 }, maxRotation: 45, autoSkip: true, maxTicksLimit: 20 } },
        },
    };

    // Weekly completions (Bar)
    const weeklyBarData = {
        labels: completion.weekly.map(w => w.week),
        datasets: [{
            label: 'Complétions',
            data: completion.weekly.map(w => w.completes),
            backgroundColor: isF2f ? 'rgba(59, 130, 246, 0.7)' : 'rgba(220, 38, 38, 0.7)',
            borderRadius: 4,
        }],
    };

    const weeklyBarOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero: true, ticks: { font: { size: 10 } } },
            x: { ticks: { font: { size: 10 } } },
        },
    };

    // Control doughnut
    const controlDoughnutData = {
        labels: ['Validés', 'Rejetés', 'En attente'],
        datasets: [{
            data: [control.validated, control.rejected, control.on_hold],
            backgroundColor: ['#16a34a', '#dc2626', '#eab308'],
            borderWidth: 0,
        }],
    };

    const actionSourceMap = {
        'Validés': control.actions_by_source?.validate || { human: 0, ai: 0 },
        'Rejetés': control.actions_by_source?.reject || { human: 0, ai: 0 },
        'En attente': control.actions_by_source?.hold || { human: 0, ai: 0 },
    };

    const controlDoughnutOpts = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 }, padding: 12 } },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const split = actionSourceMap[ctx.label] || { human: 0, ai: 0 };
                        return `${ctx.label}: ${ctx.parsed} (Humain: ${split.human} · IA: ${split.ai})`;
                    },
                },
            },
        },
    };

    // Source breakdown doughnut: Humain / IA / Non contrôlé (yet)
    const sourceHumanCount = control.total_checked_human ?? control.total_checked ?? 0;
    const sourceAiOnlyCount = control.total_checked_ai_only ?? 0;
    const sourceTarget = completion.target || 0;
    const sourceUncontrolled = Math.max(0, sourceTarget - sourceHumanCount - sourceAiOnlyCount);
    const sourceDoughnutData = {
        labels: ['Humain', 'IA', 'Non contrôlé'],
        datasets: [{
            data: [sourceHumanCount, sourceAiOnlyCount, sourceUncontrolled],
            backgroundColor: ['#3b82f6', '#6366f1', '#e5e7eb'],
            borderWidth: 0,
        }],
    };
    const sourceDoughnutOpts = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 }, padding: 12 } },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const v = ctx.parsed;
                        const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                        return `${ctx.label}: ${v} (${pct}%)`;
                    },
                },
            },
        },
    };

    // Rejection reasons (Bar)
    const reasonLabels = { metier: 'Métier', projet: 'Projet', metier_et_projet: 'Métier & Projet', rien: 'Rien' };
    const reasonKeys = ['metier', 'projet', 'metier_et_projet', 'rien'];
    const reasonBarData = {
        labels: reasonKeys.map(k => reasonLabels[k]),
        datasets: [{
            label: 'Rejets',
            data: reasonKeys.map(k => control.reasons[k] || 0),
            backgroundColor: ['#dc2626', '#b91c1c', '#991b1b', '#9ca3af'],
            borderRadius: 4,
        }],
    };

    const reasonBarOpts = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const key = reasonKeys[ctx.dataIndex];
                        const split = control.reasons_by_source?.[key] || { human: 0, ai: 0 };
                        return `${ctx.label}: ${ctx.parsed.x} (Humain: ${split.human} · IA: ${split.ai})`;
                    },
                },
            },
        },
        scales: {
            x: { beginAtZero: true, ticks: { font: { size: 10 }, stepSize: 1 } },
            y: { ticks: { font: { size: 11 } } },
        },
    };

    // Weekly control (Stacked Bar)
    const controlWeeklyData = {
        labels: control.weekly.map(w => w.week),
        datasets: [
            {
                label: 'Validés',
                data: control.weekly.map(w => w.validated),
                backgroundColor: 'rgba(22, 163, 74, 0.7)',
                borderRadius: 4,
                stack: 'a',
            },
            {
                label: 'Rejetés',
                data: control.weekly.map(w => w.rejected),
                backgroundColor: 'rgba(220, 38, 38, 0.7)',
                borderRadius: 4,
                stack: 'a',
            },
        ],
    };

    const controlWeeklyOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const w = control.weekly[ctx.dataIndex] || {};
                        if (ctx.dataset.label === 'Validés') {
                            return `Validés: ${ctx.parsed.y} (Humain: ${w.validated_human ?? 0} · IA: ${w.validated_ai ?? 0})`;
                        }
                        if (ctx.dataset.label === 'Rejetés') {
                            return `Rejetés: ${ctx.parsed.y} (Humain: ${w.rejected_human ?? 0} · IA: ${w.rejected_ai ?? 0})`;
                        }
                        return `${ctx.dataset.label}: ${ctx.parsed.y}`;
                    },
                },
            },
        },
        scales: {
            y: { beginAtZero: true, stacked: true, ticks: { font: { size: 10 } } },
            x: { stacked: true, ticks: { font: { size: 10 } } },
        },
    };

    // Geographic distribution (F2F only)
    const geoDist = data.geographic_distribution;
    const geoBarData = geoDist ? {
        labels: geoDist.data.map(d => d.label),
        datasets: [{
            label: 'Complétions',
            data: geoDist.data.map(d => d.count),
            backgroundColor: 'rgba(59, 130, 246, 0.7)',
            borderRadius: 4,
        }],
    } : null;

    const geoBarOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero: true, ticks: { font: { size: 10 }, stepSize: 1 } },
            x: { ticks: { font: { size: 10 }, maxRotation: 45 } },
        },
    };

    const StatCard = ({ label, value, sub, color = 'text-gray-900' }) => (
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 text-center">
            <div className={`text-xl sm:text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
            {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center gap-2 flex-wrap">
                        <FontAwesomeIcon
                            icon={isF2f ? faPersonWalking : faPhone}
                            className={isF2f ? 'text-blue-600' : 'text-red-600'}
                        />
                        <h1 className="text-lg sm:text-xl font-bold text-gray-900 break-words">{survey.title}</h1>
                        {survey.client_name && (
                            <span className="text-sm text-gray-500">({survey.client_name})</span>
                        )}
                        {canTerminateProgress && (
                            isProgressTerminated ? (
                                <button
                                    onClick={() => applyTerminateToggle(false)}
                                    disabled={togglingTerminate}
                                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 transition-colors disabled:opacity-50"
                                    title="Réafficher cette enquête dans les tableaux de bord"
                                >
                                    Réactiver
                                </button>
                            ) : (
                                <button
                                    onClick={() => setTerminateModalOpen(true)}
                                    disabled={togglingTerminate}
                                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
                                    title="Retirer cette enquête des tableaux de bord (le contrôle reste inchangé)"
                                >
                                    Terminer
                                </button>
                            )
                        )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isF2f ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                            {isF2f ? 'Face à Face' : 'CATI'}
                        </span>
                        {isProgressTerminated && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                Terminé (masqué des tableaux de bord)
                            </span>
                        )}
                        {survey.limesurvey_id && <span>LimeSurvey #{survey.limesurvey_id}</span>}
                        {survey.date_debut && survey.date_fin && (
                            <span>{survey.date_debut} → {survey.date_fin}</span>
                        )}
                    </div>

                    {/* Progression globale */}
                    {completion.target > 0 && (
                        <div className="mt-4 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => document.getElementById('section-completion')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Progression globale</span>
                                <span className="font-semibold">
                                    {completion.total.toLocaleString('fr-FR')} / {completion.target.toLocaleString('fr-FR')}
                                    <span className="ml-1 text-gray-500">({completionPct}%)</span>
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                                <div
                                    className="h-3 rounded-full transition-all duration-500"
                                    style={{
                                        width: `${completionPct}%`,
                                        backgroundColor: completionPct >= 80 ? '#16a34a' : completionPct >= 50 ? '#eab308' : '#dc2626'
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Progression contrôle */}
                    {completion.target > 0 && (() => {
                        const controlPct = Math.min(100, Math.round((control.total_checked / completion.target) * 100));
                        const controlTargetPct = survey.control_percentage || 0;
                        const targetMet = controlTargetPct > 0 && controlPct >= controlTargetPct;
                        const humanCount = control.total_checked_human ?? control.total_checked ?? 0;
                        const aiOnlyCount = control.total_checked_ai_only ?? 0;
                        const humanWidth = Math.min(100, (humanCount / completion.target) * 100);
                        const aiWidth = Math.min(100 - humanWidth, (aiOnlyCount / completion.target) * 100);
                        const totalDoneCount = humanCount + aiOnlyCount;
                        const humanPctOfDone = totalDoneCount > 0 ? Math.round((humanCount / totalDoneCount) * 100) : 0;
                        const aiPctOfDone = totalDoneCount > 0 ? Math.round((aiOnlyCount / totalDoneCount) * 100) : 0;
                        return (
                            <div className="mt-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => document.getElementById('section-controle')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">
                                        Progression contrôle
                                        {controlTargetPct > 0 && <span className="ml-1 text-gray-400">(objectif {controlTargetPct}%)</span>}
                                    </span>
                                    <span className="font-semibold">
                                        {control.total_checked.toLocaleString('fr-FR')} / {completion.target.toLocaleString('fr-FR')}
                                        <span className="ml-1 text-gray-500">({controlPct}%)</span>
                                    </span>
                                </div>
                                <div className="relative w-full bg-gray-200 rounded-full h-3 flex overflow-hidden">
                                    <div
                                        className={`h-3 transition-all duration-500 ${targetMet && aiOnlyCount === 0 ? 'bg-green-500' : 'bg-blue-500'}`}
                                        style={{ width: `${humanWidth}%` }}
                                        title={`Humain: ${humanCount} (${humanPctOfDone}%)`}
                                    ></div>
                                    <div
                                        className="h-3 bg-indigo-500 transition-all duration-500"
                                        style={{ width: `${aiWidth}%` }}
                                        title={`IA: ${aiOnlyCount} (${aiPctOfDone}%)`}
                                    ></div>
                                    {controlTargetPct > 0 && (
                                        <div
                                            className="absolute top-0 h-full w-0.5 bg-red-500 pointer-events-none"
                                            style={{ left: `${Math.min(100, controlTargetPct)}%` }}
                                            title={`Objectif: ${controlTargetPct}%`}
                                        ></div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Budget */}
                    {survey.has_budget ? (() => {
                        const budgetPct = survey.total_budget > 0 ? Math.min(100, Math.round((survey.budget_spent / survey.total_budget) * 1000) / 10) : null;
                        return (
                            <div className="mt-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => document.getElementById('section-budget')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">Budget</span>
                                    <span className="font-semibold">
                                        {Number(survey.budget_spent).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                        <span className="text-gray-400 font-normal"> / {Number(survey.total_budget).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD</span>
                                        {budgetPct !== null && <span className="ml-1 text-gray-500">({budgetPct}%)</span>}
                                    </span>
                                </div>
                                {budgetPct !== null && (
                                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                        <div className="h-3 rounded-full transition-all duration-500" style={{
                                            width: `${budgetPct}%`,
                                            background: budgetPct >= 90 ? 'linear-gradient(90deg, #f87171, #dc2626)' : budgetPct >= 70 ? 'linear-gradient(90deg, #facc15, #eab308)' : 'linear-gradient(90deg, #22c55e, #16a34a)'
                                        }}></div>
                                    </div>
                                )}
                                {/* Category breakdown */}
                                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    {survey.budget_enqueteur > 0 && (
                                        <div className="flex justify-between"><span className="text-gray-400">Enquêteurs</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_enqueteur).toLocaleString('fr-FR')} / {Number(survey.budget_enqueteur).toLocaleString('fr-FR')}</span></div>
                                    )}
                                    {survey.budget_controle > 0 && (
                                        <div className="flex justify-between"><span className="text-gray-400">Contrôle</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_controle).toLocaleString('fr-FR')} / {Number(survey.budget_controle).toLocaleString('fr-FR')}</span></div>
                                    )}
                                    {survey.budget_deplacement > 0 && (
                                        <div className="flex justify-between"><span className="text-gray-400">Déplacement</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_deplacement).toLocaleString('fr-FR')} / {Number(survey.budget_deplacement).toLocaleString('fr-FR')}</span></div>
                                    )}
                                    {survey.budget_imprevus > 0 && (
                                        <div className="flex justify-between"><span className="text-gray-400">Imprévus</span><span className={`tabular-nums ${survey.imprevus_consumed > survey.budget_imprevus ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{Number(survey.imprevus_consumed).toLocaleString('fr-FR')} / {Number(survey.budget_imprevus).toLocaleString('fr-FR')}</span></div>
                                    )}
                                </div>
                            </div>
                        );
                    })() : (
                        <div className="mt-3 text-sm text-gray-400 italic">Budget non encore défini</div>
                    )}
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard label="Complétions" value={completion.total.toLocaleString('fr-FR')} color={isF2f ? 'text-blue-600' : 'text-red-600'} />
                    <StatCard label="Objectif" value={completion.target ? completion.target.toLocaleString('fr-FR') : '—'} />
                    <StatCard label="Moy. / Jour" value={completion.avg_per_day} sub={`${completion.active_days} jours actifs`} />
                    <StatCard
                        label="Contrôlés"
                        value={control.total_checked.toLocaleString('fr-FR')}
                        sub={completion.target > 0 ? `${Math.round((control.total_checked / completion.target) * 100)}% couverture` : '—'}
                    />
                    <StatCard
                        label="Taux Validation"
                        value={`${control.validation_rate}%`}
                        color={control.validation_rate >= 80 ? 'text-green-600' : control.validation_rate >= 60 ? 'text-yellow-600' : 'text-red-600'}
                    />
                    <StatCard
                        label="Note Moyenne"
                        value={control.avg_rating ? `${control.avg_rating}/5` : '—'}
                        color={control.avg_rating >= 4 ? 'text-green-600' : control.avg_rating >= 3 ? 'text-yellow-600' : 'text-red-600'}
                    />
                </div>

                {/* Completion Charts */}
                {completion.daily.length > 0 && (
                    <div id="section-completion" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <FontAwesomeIcon icon={faChartLine} className={isF2f ? 'text-blue-600' : 'text-red-600'} />
                            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Complétions par Jour</h2>
                        </div>
                        <div style={{ height: '300px' }}>
                            <Line data={dailyLineData} options={dailyLineOpts} />
                        </div>
                    </div>
                )}

                {completion.weekly.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Complétions par Semaine</h2>
                        <div style={{ height: '260px' }}>
                            <Bar data={weeklyBarData} options={weeklyBarOpts} />
                        </div>
                    </div>
                )}

                {/* Geographic distribution (F2F only) */}
                {isF2f && geoDist && geoDist.data.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                            Répartition par {geoDist.field === 'ville' ? 'Ville' : 'Région'}
                        </h2>
                        <div style={{ height: Math.max(260, geoDist.data.length * 30) + 'px' }}>
                            <Bar data={geoBarData} options={geoBarOpts} />
                        </div>
                    </div>
                )}

                {/* Control Quality */}
                {control.total_checked > 0 && (
                    <>
                        <div id="section-controle" className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Répartition des Contrôles card */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-green-600" />
                                    <h2 className="text-sm sm:text-base font-semibold text-gray-900">Répartition des Contrôles</h2>
                                </div>
                                <div style={{ height: '220px' }} className="flex justify-center">
                                    <Doughnut data={controlDoughnutData} options={controlDoughnutOpts} />
                                </div>
                            </div>
                            {/* Source des Contrôles card (Human / IA / Non contrôlé) */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-blue-600" />
                                    <h2 className="text-sm sm:text-base font-semibold text-gray-900">Source du Contrôle</h2>
                                </div>
                                <div style={{ height: '220px' }} className="flex justify-center">
                                    <Doughnut data={sourceDoughnutData} options={sourceDoughnutOpts} />
                                </div>
                            </div>
                            {/* Problèmes Détectés card */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-red-600" />
                                    <h2 className="text-sm sm:text-base font-semibold text-gray-900">Problèmes Détectés</h2>
                                </div>
                                {reasonKeys.some(k => (control.reasons[k] || 0) > 0) ? (
                                    <>
                                        <div className="space-y-2 mb-3">
                                            {reasonKeys.map(k => {
                                                const count = control.reasons[k] || 0;
                                                const pct = control.total_checked > 0 ? Math.round((count / control.total_checked) * 100) : 0;
                                                return count > 0 ? (
                                                    <div key={k} className="flex items-center justify-between text-xs">
                                                        <span className="text-gray-600">{reasonLabels[k]}</span>
                                                        <span className="font-semibold text-gray-800">{count} <span className="text-gray-400">({pct}%)</span></span>
                                                    </div>
                                                ) : null;
                                            })}
                                        </div>
                                        <div style={{ height: '180px' }}>
                                            <Bar data={reasonBarData} options={reasonBarOpts} />
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-xs text-gray-400 text-center">Aucune raison enregistrée</p>
                                )}
                            </div>
                        </div>

                        {/* Weekly control stacked bar */}
                        {control.weekly.length > 0 && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Contrôles par Semaine</h2>
                                <div style={{ height: '260px' }}>
                                    <Bar data={controlWeeklyData} options={controlWeeklyOpts} />
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Budget per batch chart */}
                {survey.budget_per_batch && survey.budget_per_batch.length > 0 && (() => {
                    const labels = survey.budget_per_batch.map(b => b.label);
                    const amounts = survey.budget_per_batch.map(b => b.amount);
                    const cumulative = survey.budget_per_batch.map(b => b.cumulative);

                    const budgetChartData = {
                        labels,
                        datasets: [
                            {
                                type: 'bar',
                                label: 'Montant par lot',
                                data: amounts,
                                backgroundColor: 'rgba(239, 68, 68, 0.7)',
                                borderColor: '#dc2626',
                                borderWidth: 1,
                                borderRadius: 4,
                                yAxisID: 'y',
                                order: 2,
                            },
                            {
                                type: 'line',
                                label: 'Cumul',
                                data: cumulative,
                                borderColor: '#f59e0b',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                borderWidth: 2,
                                pointRadius: 4,
                                pointBackgroundColor: '#f59e0b',
                                tension: 0.3,
                                fill: true,
                                yAxisID: 'y',
                                order: 1,
                            },
                            ...(survey.total_budget > 0 ? [{
                                type: 'line',
                                label: 'Budget total',
                                data: Array(labels.length).fill(survey.total_budget),
                                borderColor: '#10b981',
                                borderWidth: 2,
                                borderDash: [6, 4],
                                pointRadius: 0,
                                yAxisID: 'y',
                                order: 0,
                            }] : []),
                        ],
                    };

                    const handleBudgetBarClick = (_event, elements) => {
                        if (!canClickBatch || !elements || elements.length === 0) return;
                        // Find any clicked element and use its index to look up the batch
                        const idx = elements[0].index;
                        const batchId = survey.budget_per_batch[idx]?.batch_id;
                        if (batchId) navigate(`/payments/${batchId}`);
                    };

                    const budgetChartOpts = {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        onClick: canClickBatch ? handleBudgetBarClick : undefined,
                        plugins: {
                            legend: { position: 'top', labels: { usePointStyle: true, pointStyle: 'circle', padding: 15, font: { size: 11 } } },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.raw).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD`,
                                    ...(canClickBatch ? { footer: () => 'Cliquer pour voir le lot' } : {}),
                                },
                            },
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: { callback: (v) => v.toLocaleString('fr-FR') + ' MAD' },
                            },
                            x: {
                                ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } },
                            },
                        },
                    };

                    return (
                        <div id="section-budget" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Budget par Lot de Paiement</h2>
                            <div style={{ height: '300px', cursor: canClickBatch ? 'pointer' : 'default' }}>
                                <Bar data={budgetChartData} options={budgetChartOpts} />
                            </div>
                        </div>
                    );
                })()}

                {/* Empty states */}
                {completion.daily.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                        <p className="text-sm">Aucune donnée de productivité disponible pour cette enquête.</p>
                    </div>
                )}
                {control.total_checked === 0 && completion.daily.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                        <FontAwesomeIcon icon={faClipboardCheck} className="text-2xl text-gray-300 mb-2" />
                        <p className="text-sm">Aucun contrôle effectué pour cette enquête.</p>
                    </div>
                )}
            </div>
            <ConfirmationModal
                isOpen={terminateModalOpen}
                onClose={() => setTerminateModalOpen(false)}
                onConfirm={() => applyTerminateToggle(true)}
                title="Terminer l'enquête ?"
                message={`« ${survey.title} » sera retirée de tous les tableaux de bord, même si son contrôle est encore actif. Vous pourrez la réactiver depuis cette page.`}
                confirmText="Terminer"
                loading={togglingTerminate}
            />
        </div>
    );
};

export default SurveyStats;
